import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Loader2, ArrowLeft, FileJson, RefreshCw, Clipboard, Check, 
  ChevronDown, ChevronUp, Linkedin, Clock, Zap, Copy, X, Compass
} from 'lucide-react';
import { Header } from '../components/Header';
import { API_URL, cn } from '../lib/utils';

function SprintView() {
  const { sprintId } = useParams();
  const navigate = useNavigate();
  const [sprint, setSprint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedDay, setExpandedDay] = useState(1);
  const [copied, setCopied] = useState(null);
  const [regenerating, setRegenerating] = useState(null);
  const [regenInstructions, setRegenInstructions] = useState('');
  const [showRegenModal, setShowRegenModal] = useState(null);
  const [showScheduleModal, setShowScheduleModal] = useState(null);
  const [showHookModal, setShowHookModal] = useState(null);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('09:00');
  const [scheduling, setScheduling] = useState(false);
  const [posting, setPosting] = useState(false);
  const [selectingHook, setSelectingHook] = useState(false);

  useEffect(() => { fetchSprint(); }, [sprintId]);

  const fetchSprint = async () => {
    try {
      const response = await fetch(`${API_URL}/api/sprints/${sprintId}`, { credentials: 'include' });
      if (response.ok) setSprint(await response.json());
      else navigate('/dashboard');
    } catch { navigate('/dashboard'); }
    finally { setLoading(false); }
  };

  const copyToClipboard = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(sprint.output, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `sprint-${sprint.sprint_id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const regenerateDay = async (dayNum) => {
    setRegenerating(dayNum);
    setShowRegenModal(null);
    try {
      const response = await fetch(`${API_URL}/api/sprints/${sprintId}/regenerate/${dayNum}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ instructions: regenInstructions })
      });
      if (response.ok) {
        const newDay = await response.json();
        setSprint(prev => ({
          ...prev,
          output: { ...prev.output, days: prev.output.days.map((d, i) => i === dayNum - 1 ? newDay : d) }
        }));
      }
    } catch { alert('Failed to regenerate'); }
    finally { setRegenerating(null); setRegenInstructions(''); }
  };

  const schedulePost = async (dayNum) => {
    setScheduling(true);
    try {
      const response = await fetch(`${API_URL}/api/linkedin/schedule`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sprint_id: sprintId, day_num: dayNum, scheduled_date: scheduleDate, scheduled_time: scheduleTime })
      });
      if (response.ok) {
        alert('Post scheduled successfully!');
        setShowScheduleModal(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to schedule');
      }
    } catch { alert('Failed to schedule'); }
    finally { setScheduling(false); }
  };

  const postNow = async (dayNum) => {
    if (!window.confirm('Post this content to LinkedIn now?')) return;
    setPosting(dayNum);
    try {
      const response = await fetch(`${API_URL}/api/linkedin/post-now`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sprint_id: sprintId, day_num: dayNum })
      });
      if (response.ok) {
        alert('Posted to LinkedIn successfully!');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to post');
      }
    } catch { alert('Failed to post'); }
    finally { setPosting(false); }
  };

  const selectHook = async (dayNum, hookId) => {
    setSelectingHook(true);
    try {
      const response = await fetch(`${API_URL}/api/sprints/${sprintId}/select-hook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ day_num: dayNum, hook_id: hookId })
      });
      if (response.ok) {
        // Update local state
        setSprint(prev => ({
          ...prev,
          output: {
            ...prev.output,
            days: prev.output.days.map((d, i) => 
              i === dayNum - 1 ? { ...d, selectedHook: hookId } : d
            )
          }
        }));
        setShowHookModal(null);
      }
    } catch { alert('Failed to select hook'); }
    finally { setSelectingHook(false); }
  };

  if (loading) return <div className="min-h-screen bg-main flex items-center justify-center"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  if (!sprint?.output) return <div className="min-h-screen bg-main flex items-center justify-center"><p className="text-muted">Sprint not found</p></div>;

  const dayLabels = ['Common Challenges', 'Your Story', 'Core Framework', 'Deep Dive', 'Proof, Results', 'Advanced Tips', 'Natural Offer'];

  return (
    <div className="min-h-screen bg-main">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-muted hover:text-foreground mb-4">
            <ArrowLeft className="w-5 h-5" /> Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-3xl font-bold text-foreground mb-2">{sprint.inputs?.userName}'s Sprint</h1>
              {sprint.output?.sprintOverview && <p className="text-muted">{sprint.output.sprintOverview.theme}</p>}
            </div>
            <button onClick={downloadJSON} className="btn-secondary flex items-center gap-2">
              <FileJson className="w-4 h-4" /> Export
            </button>
          </div>
        </motion.div>

        <div className="grid gap-4">
          {sprint.output?.days?.map((day, index) => (
            <motion.div key={day.day} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="card overflow-hidden">
              <button onClick={() => setExpandedDay(expandedDay === day.day ? null : day.day)} className="w-full flex items-center justify-between p-4 hover:bg-card-hover transition-colors">
                <div className="flex items-center gap-4">
                  <div className={cn("w-12 h-12 rounded-xl flex items-center justify-center font-mono font-bold text-lg", expandedDay === day.day ? "bg-primary text-white" : "bg-card-hover text-muted")}>
                    {day.day}
                  </div>
                  <div className="text-left">
                    <div className="text-xs uppercase tracking-wider text-muted mb-1">Day {day.day}, {dayLabels[index]}</div>
                    <h3 className="font-heading font-semibold text-foreground text-lg">{day.title}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {regenerating === day.day && <Loader2 className="w-4 h-4 text-primary animate-spin" />}
                  {expandedDay === day.day ? <ChevronUp className="w-5 h-5 text-muted" /> : <ChevronDown className="w-5 h-5 text-muted" />}
                </div>
              </button>

              <AnimatePresence>
                {expandedDay === day.day && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="border-t border-border">
                    <div className="p-6 space-y-6">
                      <div className="flex justify-end gap-2 flex-wrap">
                        <button onClick={() => postNow(day.day)} disabled={posting === day.day} className="flex items-center gap-2 text-sm text-[#0077b5] hover:underline disabled:opacity-50">
                          {posting === day.day ? <Loader2 className="w-4 h-4 animate-spin" /> : <Linkedin className="w-4 h-4" />} Post Now
                        </button>
                        <button onClick={() => setShowScheduleModal(day.day)} className="flex items-center gap-2 text-sm text-[#0077b5] hover:underline">
                          <Clock className="w-4 h-4" /> Schedule
                        </button>
                        <button onClick={() => setShowHookModal(day.day)} className="flex items-center gap-2 text-sm text-amber-500 hover:underline">
                          <Zap className="w-4 h-4" /> A/B Hooks
                        </button>
                        <button onClick={() => setShowRegenModal(day.day)} className="flex items-center gap-2 text-sm text-muted hover:text-primary">
                          <RefreshCw className="w-4 h-4" /> Regenerate
                        </button>
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <h4 className="font-heading font-semibold text-foreground">Full Post</h4>
                          <button onClick={() => copyToClipboard(day.fullPost, `post-${day.day}`)} className="flex items-center gap-2 text-sm text-muted hover:text-primary">
                            {copied === `post-${day.day}` ? <><Check className="w-4 h-4 text-emerald-500" /> Copied!</> : <><Clipboard className="w-4 h-4" /> Copy</>}
                          </button>
                        </div>
                        <div className="bg-card-hover rounded-lg p-4 text-foreground whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto">
                          {day.fullPost}
                        </div>
                      </div>

                      {day.mainCta && (
                        <div>
                          <h4 className="font-heading font-semibold text-foreground mb-3">CTA (Call-to-Action)</h4>
                          <div className="bg-primary/10 border border-primary/20 rounded-lg p-4 text-primary">{day.mainCta}</div>
                        </div>
                      )}

                      {/* Hook Variants (new A/B system) */}
                      {day.hookVariants?.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-heading font-semibold text-foreground">Hook Variants</h4>
                            {day.selectedHook && (
                              <span className="text-xs px-2 py-1 bg-emerald-500/10 text-emerald-500 rounded-full">
                                Selected: {day.hookVariants.find(h => h.id === day.selectedHook)?.style || day.selectedHook}
                              </span>
                            )}
                          </div>
                          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {day.hookVariants.slice(0, 3).map((variant) => (
                              <div 
                                key={variant.id} 
                                className={cn(
                                  "bg-card-hover rounded-lg p-3 text-sm border-2 transition-colors",
                                  day.selectedHook === variant.id ? "border-primary" : "border-transparent hover:border-border"
                                )}
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-medium text-primary">{variant.style}</span>
                                  <button onClick={() => copyToClipboard(variant.text, `variant-${day.day}-${variant.id}`)} className="text-muted hover:text-primary">
                                    {copied === `variant-${day.day}-${variant.id}` ? <Check className="w-3 h-3 text-emerald-500" /> : <Copy className="w-3 h-3" />}
                                  </button>
                                </div>
                                <p className="text-foreground text-xs leading-relaxed line-clamp-4">{variant.text}</p>
                              </div>
                            ))}
                          </div>
                          <button 
                            onClick={() => setShowHookModal(day.day)}
                            className="mt-3 text-sm text-primary hover:underline"
                          >
                            View all {day.hookVariants.length} variants →
                          </button>
                        </div>
                      )}

                      {/* Legacy altHooks for backward compatibility */}
                      {!day.hookVariants && day.altHooks?.length > 0 && (
                        <div>
                          <h4 className="font-heading font-semibold text-foreground mb-3">Alt Hooks</h4>
                          <div className="grid gap-2">
                            {day.altHooks.map((hook, i) => (
                              <div key={i} className="bg-card-hover rounded-lg p-3 text-foreground text-sm flex justify-between gap-3">
                                <span>{hook}</span>
                                <button onClick={() => copyToClipboard(hook, `hook-${day.day}-${i}`)} className="text-muted hover:text-primary">
                                  {copied === `hook-${day.day}-${i}` ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Suggested Emojis */}
                      {day.suggestedEmojis?.length > 0 && (
                        <div>
                          <h4 className="font-heading font-semibold text-foreground mb-3">Suggested Emojis</h4>
                          <div className="flex flex-wrap gap-2">
                            {day.suggestedEmojis.map((emoji, i) => (
                              <button 
                                key={i} 
                                onClick={() => copyToClipboard(emoji, `emoji-${day.day}-${i}`)} 
                                className="px-3 py-2 bg-card-hover rounded-lg text-xl hover:bg-primary/10 transition-colors flex items-center gap-2"
                                title="Click to copy"
                              >
                                {emoji}
                                {copied === `emoji-${day.day}-${i}` && <Check className="w-3 h-3 text-emerald-500" />}
                              </button>
                            ))}
                          </div>
                          <p className="text-xs text-muted mt-2">Click to copy. Use 1-2 emojis per post for professional tone.</p>
                        </div>
                      )}

                      {/* Visual Ideas */}
                      {(day.imagePrompt || day.visualTools) && (
                        <div>
                          <h4 className="font-heading font-semibold text-foreground mb-3">Visual Ideas</h4>
                          {day.imagePrompt && (
                            <div className="bg-card-hover rounded-lg p-4 mb-3">
                              <div className="flex justify-between items-start gap-3">
                                <div>
                                  <p className="text-sm font-medium text-foreground mb-1">Image Prompt</p>
                                  <p className="text-sm text-muted">{day.imagePrompt}</p>
                                </div>
                                <button onClick={() => copyToClipboard(day.imagePrompt, `prompt-${day.day}`)} className="text-muted hover:text-primary shrink-0">
                                  {copied === `prompt-${day.day}` ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                                </button>
                              </div>
                            </div>
                          )}
                          {day.visualTools && (
                            <div className="grid sm:grid-cols-2 gap-2">
                              {day.visualTools.map((tool, i) => (
                                <a 
                                  key={i}
                                  href={tool.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-3 p-3 bg-card-hover rounded-lg hover:bg-primary/10 transition-colors group"
                                >
                                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                                    <Compass className="w-4 h-4 text-primary" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-foreground group-hover:text-primary truncate">{tool.name}</p>
                                    <p className="text-xs text-muted truncate">{tool.tip}</p>
                                  </div>
                                </a>
                              ))}
                            </div>
                          )}
                          <p className="text-xs text-muted mt-2">Copy the image prompt above and paste into any free tool to create professional visuals.</p>
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </main>

      {/* Regenerate Modal */}
      <AnimatePresence>
        {showRegenModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowRegenModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-card border border-border rounded-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
              <h3 className="font-heading font-semibold text-foreground mb-4">Regenerate Day {showRegenModal}</h3>
              <textarea value={regenInstructions} onChange={(e) => setRegenInstructions(e.target.value)} placeholder="e.g., Make it more storytelling, add specific example about..." className="textarea w-full h-28 mb-4" />
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowRegenModal(null)} className="btn-secondary">Cancel</button>
                <button onClick={() => regenerateDay(showRegenModal)} className="btn-primary">Regenerate</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Schedule Modal */}
      <AnimatePresence>
        {showScheduleModal && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowScheduleModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-card border border-border rounded-2xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center gap-3 mb-4">
                <Linkedin className="w-6 h-6 text-[#0077b5]" />
                <h3 className="font-heading font-semibold text-foreground">Schedule Day {showScheduleModal} to LinkedIn</h3>
              </div>
              <div className="grid gap-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Date</label>
                  <input type="date" value={scheduleDate} onChange={(e) => setScheduleDate(e.target.value)} className="input w-full" min={new Date().toISOString().split('T')[0]} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Time</label>
                  <input type="time" value={scheduleTime} onChange={(e) => setScheduleTime(e.target.value)} className="input w-full" />
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <button onClick={() => setShowScheduleModal(null)} className="btn-secondary">Cancel</button>
                <button onClick={() => schedulePost(showScheduleModal)} disabled={scheduling || !scheduleDate} className="btn-primary flex items-center gap-2">
                  {scheduling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
                  Schedule
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* A/B Hook Variants Modal */}
      <AnimatePresence>
        {showHookModal && sprint?.output?.days && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowHookModal(null)}>
            <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} exit={{ scale: 0.95 }} className="bg-card border border-border rounded-2xl w-full max-w-4xl max-h-[80vh] overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className="w-6 h-6 text-amber-500" />
                    <h3 className="font-heading font-semibold text-foreground">A/B Test Hooks - Day {showHookModal}</h3>
                  </div>
                  <button onClick={() => setShowHookModal(null)} className="text-muted hover:text-foreground">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-sm text-muted mt-2">Compare different hook styles and select your favorite for this post.</p>
              </div>
              <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
                {(() => {
                  const day = sprint.output.days[showHookModal - 1];
                  if (!day?.hookVariants) return <p className="text-muted">No hook variants available. Regenerate this day to get A/B test options.</p>;
                  return (
                    <div className="grid md:grid-cols-2 gap-4">
                      {day.hookVariants.map((variant) => (
                        <div 
                          key={variant.id}
                          className={cn(
                            "bg-card-hover rounded-xl p-4 border-2 transition-all cursor-pointer hover:shadow-lg",
                            day.selectedHook === variant.id ? "border-primary ring-2 ring-primary/20" : "border-transparent hover:border-border"
                          )}
                          onClick={() => selectHook(showHookModal, variant.id)}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">{variant.style}</span>
                            <div className="flex items-center gap-2">
                              {day.selectedHook === variant.id && (
                                <span className="text-xs text-emerald-500 flex items-center gap-1">
                                  <Check className="w-3 h-3" /> Selected
                                </span>
                              )}
                              <button 
                                onClick={(e) => { e.stopPropagation(); copyToClipboard(variant.text, `modal-${variant.id}`); }} 
                                className="text-muted hover:text-primary p-1"
                              >
                                {copied === `modal-${variant.id}` ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          <p className="text-foreground text-sm leading-relaxed whitespace-pre-wrap">{variant.text}</p>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default SprintView;
